// Ce se poate deschide în vizualizator cât timp lanțul macină.
//
// Un proiect nou pornește din temă cu UN job — planul de situație —, iar restul
// vine singur, în ordine: pe fiecare așezare o arhitectură, pe fiecare
// arhitectură o schiță de structură (v. `control-plane/services/inlantuire.py`).
// Tab-urile de disciplină ale Studioului, care se stingeau cât se genera, au
// plecat odată cu Studioul-cu-taburi: lanțul din antet spune acum, pe fiecare
// etapă, ce e în lucru și ce așteaptă (v. `nav/selectie.numaratori`). Aici au
// rămas cele două întrebări ale vizualizatorului:
//
//   * macheta planului de situație se poate compune? — din arhitectura
//     construită PE el, gata, în lucru sau pe drum;
//   * importul planșelor de arhitectură a picat? — atunci ecranul fără
//     arhitectură spune asta, nu «nicio soluție».
//
// Patru stări:
//
//   gata      există cel puțin o soluție `ready`;
//   in_lucru  o rulare chiar macină pentru ea (rând în lucru, sau rulare fără
//             rând încă);
//   la_rand   macină ceva din AMONTE, iar lanțul vine sigur peste el;
//   liber     nimic nu vine.

import type { Job } from "../api";
import type { Rulare, SolutieRand } from "../nav/api";
import { eDeschis } from "../nav/selectie";

export type StareGenerare = "gata" | "in_lucru" | "la_rand" | "liber";

export const seAsteapta = (s: StareGenerare) => s === "in_lucru" || s === "la_rand";

const rulareVie = (j: Job) => (j.status === "running" || j.status === "pending") && !j.is_dead;

/** Macheta planului de situație se compune din arhitectura construită PE el
 *  (v. `Vizualizator`, efectul care o aduce). Cât timp aceea nu e gata, dar
 *  vine, 3D-ul se stinge. Pe ACEST plan, nu pe proiect: o căutare propune mai
 *  multe așezări, iar arhitecturile lor ies pe rând. `fantome` sunt rulările
 *  registrului care n-au încă rând (`rulari_fara_solutie`). */
export function stareMachetaSituatie(
  situatie: SolutieRand, solutii: SolutieRand[], fantome: Rulare[],
): StareGenerare {
  const rude = solutii.filter((s) => s.discipline === "architecture"
                                     && s.lineage.site_plan === situatie.id);
  // …sau cu etajele deja scrise (jobul ei stă în `waiting` după Pasul 1,
  // v. `stareDinStatus`, ori rulează deja pasul 2 — `pasulDoi`): fațada,
  // ferestrele și balcoanele sunt în ele, iar parcarea care se mai caută nu
  // schimbă nimic din ce arată macheta
  // …sau cu un job DE DUPĂ ETAJE pe ea (agentul de fațadă, recompartimentarea):
  // ele scriu pe o arhitectură deja ridicată, deci macheta are ce arăta — ținută
  // «în lucru» cât rula recomp-ul, butonul 3D clipea încă o jumătate de oră după
  // ce agentul de fațadă spusese «am terminat» (Cătălin, 20.10.2026)
  if (rude.some((s) => s.status === "ready" || s.rulare?.status === "waiting"
                       || (!!s.rulare && (pasulDoi(s.rulare) || DUPA_ETAJE.has(s.rulare.kind))))) {
    return "gata";
  }
  if (rude.some((s) => eDeschis(s.stare))
      || fantome.some((j) => (j.kind === "architecture" || j.kind === "external_architecture")
                             && j.upstream_solution_id === situatie.id)) {
    return "in_lucru";
  }
  if (situatie.in_lant) return "la_rand";
  return "liber";
}

/** Are planul ăsta o arhitectură SCRISĂ pe el? Numai atunci se deschide
 *  macheta: fără ea, 3D-ul ar arăta doar volumele albe ale planului, care nu
 *  spun nimic din ce se caută pe machetă. NU se așteaptă «gata»:
 *  rândul arhitecturii se naște odată cu documentul ei, la capătul etajelor
 *  pasului 1, cu fațada compusă pe ele (`architecture/service._scrie_solutia`),
 *  deci din clipa aceea macheta are ce arăta, iar agentul de fațadă se vede
 *  lucrând pe ea. Stările cu desen sunt cele pe care Studioul le deschide
 *  (`pages/Studio`, `areDesen`). */
export function machetaAreCeArata(situatie: SolutieRand, solutii: SolutieRand[]): boolean {
  return solutii.some((s) => s.discipline === "architecture"
                             && s.lineage.site_plan === situatie.id && areDesen(s));
}

/** Rândul are document? Aceeași întrebare pentru butonul machetei și pentru
 *  arhitectura adusă pe ea (`viewer/useArhitecturaInlantuita`): dacă butonul
 *  ar socoti o stare pe care aducerea o sare, 3D-ul s-ar deschide tocmai pe
 *  volumele albe pe care butonul le ține închise. */
export const areDesen = (s: SolutieRand): boolean =>
  s.status === "ready" || s.status === "running" || s.status === "nonconvergent";

/** Joburile care rulează PE o arhitectură cu etajele scrise (v.
 *  `control-plane/services/inlantuire.VERIGI`): nu nasc soluție, nu golesc macheta. */
const DUPA_ETAJE = new Set(["facade", "recomp"]);

/** ȘI CÂT RULEAZĂ PASUL 2. Jobul care și-a terminat așteptarea e REPORNIT pe
 *  aceeași soluție, cu `step: 2` (v. `control-plane/api/jobs.py`, reluarea),
 *  deci primul pas e tot scris — doar că jobul nu mai stă în `waiting`. Fără
 *  asta, clipa în care schița de structură se termina și arhitectura își
 *  pornea parcarea stingea macheta până la capătul parcării (Catalin,
 *  28.09.2026: «când apăs pe etajul 9 mă bagă iar în loading screen»). */
function pasulDoi(r: Rulare): boolean {
  return (r.status === "running" || r.status === "pending")
    && ((r.step ?? 0) >= 2 || (r.step_index ?? 0) >= 2);
}

/** Importul planșelor de arhitectură care N-A ADUS arhitectura: a picat, a
 *  fost anulat sau și-a pierdut pulsul, iar proiectul n-are nicio arhitectură
 *  gata. Numai cel mai nou import contează — unul picat de acum o lună, peste
 *  care s-a importat din nou, nu mai e o veste. `null` când nu e nimic de spus. */
export function importulAPicat(solutii: SolutieRand[], jobs: Job[]): Job | null {
  if (solutii.some((s) => s.discipline === "architecture" && s.status === "ready")) {
    return null;
  }
  const ultim = jobs.filter((j) => j.kind === "external_architecture")
    .sort((a, b) => Date.parse(b.created_at || "") - Date.parse(a.created_at || ""))[0];
  if (!ultim) return null;
  const picat = ultim.status === "failed" || ultim.status === "cancelled"
    || ((ultim.status === "running" || ultim.status === "pending") && ultim.is_dead);
  return picat ? ultim : null;
}

/** Importul e încă în lucru (fără puls pierdut). */
export const importulMacina = (jobs: Job[]) =>
  jobs.some((j) => j.kind === "external_architecture" && rulareVie(j));
